import React from "react";
import { Modal, Toggle } from "@fluentui/react";
import { DocumentIcon, DeleteIcon, CancelIcon } from "@fluentui/react-icons";
import * as S from "../Styles/ImportCompositionStyles";
import { processFile, downloadExcelTemplate, downloadCSVTemplate, formatDateForDisplay } from "../Functions/Imports";
import ElementsIAIcon from "../../../../images/ia.svg";
import { ChevronDownIcon, ChevronUpIcon } from "@fluentui/react-icons-mdl2";
import ElementService from "../../../../services/ElementsService";
import { AllElementsAmountFromUser } from "../../../../services/ElementsService";
import { decimalFormatter } from "../../../../utils/numberFormatter";
import portfolioModelWallets from "../../../../services/PortfolioModelWalletService";
import { CompositionFromBody } from "../../../../services/PortfolioModelWalletService";
import { IDetectedColumns, IFileData, IImportCompositionProps, IImportedRow, IPortfolioModelModificationState } from "../Interfaces/SummaryPortfolioInterfaces";
import { validateImportedCompositionDates } from "../Functions/CompositionDateValidation";

// Constante para el precio de la importación de composiciones en Elements IA
const elementsIAPrice = 500;

const ImportComposition: React.FC<IImportCompositionProps> = ({isOpen, onClose, onImport, portfolioModelName, portfolioModelId, userId, existingCompositions = []}) =>
{
    const [selectedFile, setSelectedFile] = React.useState<IFileData | null>(null);
    const [fileType, setFileType] = React.useState<'excel' | 'csv'>('csv');
    const [importMode, setImportMode] = React.useState<'replace' | 'append'>('replace');
    const [dateHandling, setDateHandling] = React.useState<'auto' | 'manual'>('auto');
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errors, setErrors] = React.useState<string[]>([]);
    const [warnings, setWarnings] = React.useState<string[]>([]);
    const [dragOver, setDragOver] = React.useState(false);
    const [importedData, setImportedData] = React.useState<IImportedRow[]>([]);
    const [detectedColumns, setDetectedColumns] = React.useState<IDetectedColumns | null>(null);
    const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
    const [currentStep, setCurrentStep] = React.useState<'preview' | 'edit'>('preview');
    const [editableData, setEditableData] = React.useState<any[]>([]);
    const [savingsPlansData, setSavingsPlansData] = React.useState<AllElementsAmountFromUser[]>([]);
    const [isImporting, setIsImporting] = React.useState<boolean>(false);
    const [importResult, setImportResult] = React.useState<{status: boolean, message: string} | null>(null);
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);
    const [portfolioModelModification, setPortfolioModelModification] = React.useState<IPortfolioModelModificationState>(
    {
        userRequestPortfolioModelChange: false,
        selectedCompositionDate: null,
        validationInfo: null
    });
    
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const errorTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const hasLoadedSavingsPlansRef = React.useRef(false);
    const successTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Función para cargar datos de planes de ahorro
    const loadSavingsPlansData = React.useCallback(async (forceReload = false) =>
    {
        if(!userId || (hasLoadedSavingsPlansRef.current && !forceReload))
        {
            return;
        }

        try
        {
            const data = await ElementService.getAllElementsAmountFromUser(userId);
            setSavingsPlansData(data);
            hasLoadedSavingsPlansRef.current = true;
        }
        catch(error)
        {
            setSavingsPlansData([]);
        }
    }, [userId]);

    // Cargar datos de planes de ahorro al montar el componente
    React.useEffect(() =>
    {
        loadSavingsPlansData();
    }, [loadSavingsPlansData]);

    // Validar fechas importadas cuando se procese el archivo
    React.useEffect(() =>
    {
        if(importedData.length > 0 && existingCompositions.length > 0)
        {
            const dates = importedData.map(row => formatDateForDisplay(row.fecha));
            
            const validation = validateImportedCompositionDates(dates, existingCompositions);
            
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: validation.selectedCompositionDate,
                validationInfo: validation,
                userRequestPortfolioModelChange: validation.isInRange ? prev.userRequestPortfolioModelChange : false
            }));
        }
        else if(importedData.length > 0)
        {
            // Reseteamos el estado si no hay composiciones existentes
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: null,
                validationInfo: null,
                userRequestPortfolioModelChange: false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importedData]);

    // Validar cuando se carguen las composiciones existentes
    React.useEffect(() =>
    {
        if(importedData.length > 0 && existingCompositions.length > 0)
        {
            const dates = importedData.map(row => formatDateForDisplay(row.fecha));
            const validation = validateImportedCompositionDates(dates, existingCompositions);
            
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: validation.selectedCompositionDate,
                validationInfo: validation,
                userRequestPortfolioModelChange: validation.isInRange ? prev.userRequestPortfolioModelChange : false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingCompositions.length]);

    // Limpiar errores después de 10 segundos
    React.useEffect(() =>
    {
        if(errors.length > 0)
        {
            // Limpiar timeout anterior si existe
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
            }
            
            // Establecer nuevo timeout para limpiar errores en 10 segundos
            errorTimeoutRef.current = setTimeout(() =>
            {
                setErrors([]);
                errorTimeoutRef.current = null;
            }, 10000);
        }
        
        // Cleanup function
        return () =>
        {
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
                errorTimeoutRef.current = null;
            }
        };
    }, [errors]);

    // Cleanup de timeouts al desmontar el componente
    React.useEffect(() =>
    {
        return () =>
        {
            if(successTimeoutRef.current)
            {
                clearTimeout(successTimeoutRef.current);
                successTimeoutRef.current = null;
            }
        };
    }, []);

    const handleClose = React.useCallback(() =>
    {
        // Limpiar timeouts inmediatamente
        if(errorTimeoutRef.current)
        {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
        if(successTimeoutRef.current)
        {
            clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = null;
        }
        
        // Resetear estados críticos inmediatamente para evitar re-renders problemáticos
        setCurrentStep('preview');
        setEditableData([]);
        setIsProcessing(false);
        setIsImporting(false);
        setErrors([]);
        setWarnings([]);
        setImportResult(null);
        setShowSuccessMessage(false);
        setShowErrorMessage(false);
        setDragOver(false);
        // No limpiar savingsPlansData para mantener los datos persistentes
        
        // Cerrar el modal
        onClose();
        
        // Limpiar el resto de estados después de un delay para evitar efectos visuales
        setTimeout(() =>
        {
            setSelectedFile(null);
            setImportMode('replace');
            setDateHandling('auto');
            setImportedData([]);
            setDetectedColumns(null);
            setExpanded({});
        }, 300);
    }, [onClose]);

    const handleFileTypeChange = React.useCallback((e: any, checked?: boolean) =>
    {
        const newFileType = checked ? 'excel' : 'csv';
        setFileType(newFileType);
        
        // Limpiar errores y warnings al cambiar tipo de archivo
        setErrors([]);
        setWarnings([]);
        
        // Limpiar archivo seleccionado si existe
        if(selectedFile)
        {
            setSelectedFile(null);
            setImportedData([]);
            setDetectedColumns(null);
            if(fileInputRef.current)
            {
                fileInputRef.current.value = '';
            }
        }
        
        // Limpiar timeout de errores
        if(errorTimeoutRef.current)
        {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, [selectedFile]);

    const handleDownloadTemplate = React.useCallback((event?: React.MouseEvent) =>
    {
        if(event)
        {
            event.preventDefault();
            event.stopPropagation();
        }
        
        try
        {
            if(fileType === 'excel')
            {
                downloadExcelTemplate();
            }
            else
            {
                downloadCSVTemplate();
            }
        }
        catch(error)
        {
            setErrors(['Error al descargar la plantilla. Verifica que las descargas estén permitidas en tu navegador.']);
        }
    }, [fileType]);

    const handleFileSelect = React.useCallback(async (file: File) =>
    {
        if(!file)
        {
            return;
        }

        // Validar tipo de archivo según la selección
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        const validExtensions = fileType === 'excel' ? ['.xlsx', '.xls'] : ['.csv'];
        
        if(!validExtensions.includes(fileExtension))
        {
            const expectedFormats = fileType === 'excel' ? 'Excel (.xlsx, .xls)' : 'CSV (.csv)';
            setErrors([`El archivo debe ser de tipo ${expectedFormats} según la selección actual.`]);
            return;
        }

        // Validar tamaño del archivo (máximo 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB en bytes
        if(file.size > maxSize)
        {
            setErrors(['El archivo es demasiado grande. El tamaño máximo permitido es 10MB.']);
            return;
        }

        setErrors([]);
        setWarnings([]);
        setIsProcessing(true);
        setImportedData([]);
        setDetectedColumns(null);

        try
        {
            // Procesar el archivo usando las funciones de Imports.tsx
            const result = await processFile(file, fileType);
            
            // Guardar los datos procesados
            setImportedData(result.processedData);
            setDetectedColumns(result.detectedColumns);
            setErrors(result.errors);
            setWarnings(result.warnings);
            
            setSelectedFile(
            {
                name: file.name,
                size: file.size,
                content: result.processedData.map(item => `${item.fecha},${item.isin},${item.weight}%`),
                headers: ['Fecha', 'ISIN', 'Peso', 'Disponible']
            });
            
            setIsProcessing(false);
        }
        catch(error)
        {
            setErrors([error instanceof Error ? error.message : 'Error procesando el archivo']);
            setIsProcessing(false);
            setImportedData([]);
            setDetectedColumns(null);
        }
    }, [fileType]);

    const handleDrop = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(false);
        
        const files = Array.from(e.dataTransfer.files);
        if(files.length > 0)
        {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleDragOver = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(false);
    }, []);

    const handleFileInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const files = e.target.files;
        if(files && files.length > 0)
        {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleRemoveFile = React.useCallback(() =>
    {
        setSelectedFile(null);
        setErrors([]);
        setWarnings([]);
        setImportedData([]);
        setDetectedColumns(null);
        if(fileInputRef.current)
        {
            fileInputRef.current.value = '';
        }
        
        // Limpiar timeout de errores
        if(errorTimeoutRef.current)
        {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, []);

    const handleContinue = React.useCallback(async () =>
    {
        if(!importedData.length)
        {
            return;
        }

        // Si no hay datos de planes de ahorro cargados, cargarlos primero
        if(savingsPlansData.length === 0 && userId && !hasLoadedSavingsPlansRef.current)
        {
            try
            {
                await loadSavingsPlansData();
                
                // Convertir datos de planes de ahorro al formato de la tabla en el segundo paso
                const dataFromSavingsPlans = savingsPlansData.map(plan =>
                ({
                    planAhorro: plan.planName,
                    elementsDisponibles: plan.ia,
                    elementsAUsar: 0
                }));

                setEditableData(dataFromSavingsPlans);
                setCurrentStep('edit');
            }
            catch(error)
            {
                setErrors(['Error al cargar los planes de ahorro. Por favor, inténtalo de nuevo.']);
            }
            return;
        }

        // Convertir datos de planes de ahorro al formato de la tabla editable
        const editableDataFromSavingsPlans = savingsPlansData.map(plan =>
        ({
            planAhorro: plan.planName,
            elementsDisponibles: plan.ia,
            elementsAUsar: 0
        }));

        setEditableData(editableDataFromSavingsPlans);
        setCurrentStep('edit');
    }, [importedData, savingsPlansData, userId, loadSavingsPlansData]);

    const handleEditableDataChange = React.useCallback((index: number, field: string, value: any) =>
    {
        setEditableData(prevData =>
        {
            const newData = [...prevData];
            newData[index] = { ...newData[index], [field]: value };
            return newData;
        });
    }, []);

    // Función para manejar el cambio del checkbox de modificación de Cartera Modelo
    const handlePortfolioModelModificationChange = React.useCallback((event: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        setPortfolioModelModification(prev => ({
            ...prev,
            userRequestPortfolioModelChange: checked || false
        }));
    }, []);

    const handleImport = React.useCallback(async () =>
    {
        if(!selectedFile || editableData.length === 0 || !portfolioModelId)
        {
            return;
        }

        try
        {
            setIsImporting(true);
            setErrors([]);
            setWarnings([]);

            // Crear el mapeo de Elements usados por plan de ahorro
            const elementsUsed: {[key: number]: number} = {};
            editableData.forEach(plan => 
            {
                if(plan.elementsAUsar > 0)
                {
                    const planId = savingsPlansData.findIndex(sp => sp.planName === plan.planAhorro);
                    if(planId !== -1)
                    {
                        let savingPlanId = savingsPlansData.find(sp => sp.planName === plan.planAhorro)?.savingPlanId;
                        if(savingPlanId)
                        {
                            elementsUsed[savingPlanId] = plan.elementsAUsar;
                        }
                        else
                        {
                            setErrors(['Error al importar la composición. Por favor, inténtalo de nuevo.']);
                            setIsImporting(false);
                            return;
                        }
                    }
                }
            });

            // Crear las composiciones agrupadas por fecha
            const groupedDataForComposition = importedData.reduce((acc, row) =>
            {
                const fecha = formatDateForDisplay(row.fecha);
                if(!acc[fecha])
                {
                    acc[fecha] = [];
                }
                acc[fecha].push(row);
                return acc;
            }, {} as Record<string, typeof importedData>);

            const composition = Object.entries(groupedDataForComposition).map(([fecha, rows]) => 
            {
                const isinAndWeight: {[key: string]: number} = {};
                rows.forEach(row => 
                {
                    isinAndWeight[row.isin] = row.weight;
                });

                return{
                    date: fecha,
                    isinAndWeight: isinAndWeight
                };
            });

            // Crear el objeto CompositionFromBody
            const compositionBody: CompositionFromBody = 
            {
                elementsUsed: elementsUsed,
                composition: composition,
                isImportation: true,
                portfolioModelId: portfolioModelId,
                ...(portfolioModelModification.userRequestPortfolioModelChange && {userRequestPortfolioModelChange: true})
            };

            // Llamar al servicio para crear las composiciones
            const result = await portfolioModelWallets.createCompositions(compositionBody);
            setImportResult(result);

            // Manejar la respuesta del servidor
            if(result.status === true)
            {
                // Mostrar mensaje de éxito y deshabilitar botones
                setIsImporting(false);
                setShowSuccessMessage(true);
                
                // Recargar Elements IA para mostrar saldo actualizado
                await loadSavingsPlansData(true);
                
                // Limpiamos timeout anterior si existe
                if(successTimeoutRef.current)
                {
                    clearTimeout(successTimeoutRef.current);
                }
                
                // Cerramos modal después de 2 segundos
                successTimeoutRef.current = setTimeout(() =>
                {
                    const importData = 
                    {
                        file: selectedFile,
                        mode: importMode,
                        dateHandling: dateHandling,
                        portfolioModelName: portfolioModelName,
                        processedData: editableData,
                        detectedColumns: detectedColumns,
                        totalRecords: editableData.length,
                        totalWeight: editableData.reduce((sum, item) => sum + item.weight, 0)
                    };

                    // Pasar información sobre si se debe recargar datos
                    onImport(importData, portfolioModelModification.userRequestPortfolioModelChange);
                    onClose();
                    
                    // Limpiamos estados después de un pequeño delay
                    setTimeout(() =>
                    {
                        setSelectedFile(null);
                        setImportMode('replace');
                        setDateHandling('auto');
                        setIsImporting(false);
                        setErrors([]);
                        setWarnings([]);
                        setImportResult(null);
                        setShowSuccessMessage(false);
                        setShowErrorMessage(false);
                        setDragOver(false);
                        setImportedData([]);
                        setDetectedColumns(null);
                        setExpanded({});
                        setCurrentStep('preview');
                        setEditableData([]);
                        
                        // Limpiar timeouts
                        if(errorTimeoutRef.current)
                        {
                            clearTimeout(errorTimeoutRef.current);
                            errorTimeoutRef.current = null;
                        }
                        if(successTimeoutRef.current)
                        {
                            clearTimeout(successTimeoutRef.current);
                            successTimeoutRef.current = null;
                        }
                    }, 100);
                }, 2000);
            }
            else
            {
                // Error: mostramos mensaje de error del servidor y reactivamos botones
                setIsImporting(false);
                setShowErrorMessage(true);
            }
        }
        catch(error)
        {
            setErrors(['Error al importar la composición. Por favor, inténtalo de nuevo.']);
            setIsImporting(false);
        }
    }, [selectedFile, editableData, portfolioModelId, importedData, savingsPlansData, importMode, dateHandling, portfolioModelName, detectedColumns, onImport, onClose, loadSavingsPlansData, portfolioModelModification.userRequestPortfolioModelChange]);

    const formatFileSize = (bytes: number): string =>
    {
        if(bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Determinar si la validación fue exitosa
    const isValidationSuccessful = React.useMemo(() =>
    {
        return selectedFile && 
               importedData.length > 0 && 
               errors.length === 0 && 
               !isProcessing &&
               currentStep === 'preview';
    }, [selectedFile, importedData.length, errors.length, isProcessing, currentStep]);

    // Verificamos si hay fondos no disponibles
    const hasUnavailableFunds = React.useMemo(() =>
    {
        return importedData.some(row => row.available === false);
    }, [importedData]);

    // Determinar si se puede continuar (solo en paso preview)
    const canContinue = React.useMemo(() =>
    {
        return currentStep === 'preview' && 
               selectedFile && 
               importedData.length > 0 && 
               errors.length === 0 && 
               !isProcessing && 
               !isImporting &&
               !showSuccessMessage &&
               !showErrorMessage &&
               !hasUnavailableFunds;
    }, [currentStep, selectedFile, importedData.length, errors.length, isProcessing, isImporting, showSuccessMessage, showErrorMessage, hasUnavailableFunds]);

    // Calcular total de Elements IA a usar
    const totalElementsAUsar = React.useMemo(() =>
    {
        return editableData.reduce((total, row) => total + (row.elementsAUsar || 0), 0);
    }, [editableData]);

    // Determinar si se puede importar (solo en paso edit)
    const canEndImport = React.useMemo(() =>
    {
        return currentStep === 'edit' && selectedFile && totalElementsAUsar === elementsIAPrice && !isImporting && !showSuccessMessage && !showErrorMessage;
    }, [currentStep, selectedFile, totalElementsAUsar, isImporting, showSuccessMessage, showErrorMessage]);

    const modalTitle = portfolioModelName ? `Importar Composición - ${portfolioModelName}` : "Importar Composición de Cartera";

    const groupedData = React.useMemo(() =>
    {
        return importedData.reduce((acc, row) =>
        {
            const fecha = formatDateForDisplay(row.fecha);
            if(!acc[fecha])
            {
                acc[fecha] = [];
            }
            acc[fecha].push(row);
            return acc;
        }, {} as Record<string, typeof importedData>);

    }, [importedData]);

    const toggleGroup = (fecha: string) =>
    {
        setExpanded((prev) =>
        ({
            ...prev,
            [fecha]: !prev[fecha],
        }));
    };

    return(
        <React.Fragment>
            <S.FluentUIIconFix />
            <Modal isOpen={isOpen} onDismiss={handleClose} isBlocking={true} containerClassName="modal-container" styles={{main: {width: '70vw !important', maxWidth: '1600px', height: 'auto'}}}>
                <S.ModalContainer>
                    <S.ModalTitle>{modalTitle}</S.ModalTitle>
                    <S.ContentContainer>
                        <S.FirstRow>
                            <p>Selecciona un tipo de archivo para la importación de la composición de la cartera. Puedes descargar una plantilla de ejemplo para guiarte.</p>
                        </S.FirstRow>
                        <S.SecondRow>
                            <S.TemplateDownload>
                                <S.TemplateLabel>Plantilla de Ejemplo:</S.TemplateLabel>
                                <S.TemplateButton type="button" onClick={handleDownloadTemplate}>
                                    Descargar {fileType === 'excel' ? 'Excel' : 'CSV'}
                                </S.TemplateButton>
                            </S.TemplateDownload>
                            <S.CompositionPrice>
                                <S.CompositionPriceLabel>Precio de este servicio: {elementsIAPrice} Elements IA</S.CompositionPriceLabel>
                                <S.ImageElementsIA alt="IA" src={ElementsIAIcon} />
                            </S.CompositionPrice>
                            <S.FileTypeSelector>
                                <S.FileTypeLabel>Tipo de Archivo:</S.FileTypeLabel>
                                <S.FileTypeOption $isActive={fileType === 'csv'}>CSV</S.FileTypeOption>
                                <Toggle checked={fileType === 'excel'} onChange={handleFileTypeChange}
                                    styles={{root: {margin: '0 8px'}}}
                                />
                                <S.FileTypeOption $isActive={fileType === 'excel'}>Excel</S.FileTypeOption>
                            </S.FileTypeSelector>
                        </S.SecondRow>
                        {!selectedFile || (!isValidationSuccessful && currentStep === 'preview') ?
                        (
                            <S.Section>
                                <S.SectionTitle>Seleccionar Archivo {fileType === 'excel' ? 'Excel (.xlsx, .xls)' : 'CSV (.csv)'}</S.SectionTitle>
                                <S.SectionDescription>
                                    {fileType === 'excel' ?
                                    'El archivo debe incluir las siguientes columnas en este orden con cabeceras: Fecha, ISIN y Peso (Ejemplo: 22,3% o 22,3). Todos los importes sumados de la columna de Peso deben sumar 100% por fecha.' : 
                                    'El archivo debe estar separado por comas (,) o punto y coma (;) y debe seguir este orden con cabeceras: Fecha, ISIN y Peso (Ejemplo: 22,3% o 22,3). Todos los importes sumados de la columna de Peso deben sumar 100% por fecha.'}
                                </S.SectionDescription>
                                <S.FileUploadArea className={dragOver ? 'drag-over' : ''} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <DocumentIcon style={{fontSize: '36px', color: '#605e5c', marginBottom: '12px'}} />
                                    <S.FileUploadText>
                                        Haz clic aquí o arrastra un archivo para subirlo
                                    </S.FileUploadText>
                                    <S.FileUploadSubtext>
                                        Formato esperado: {fileType === 'excel' ? '.xlsx, .xls' : '.csv'} (máximo 10MB)
                                    </S.FileUploadSubtext>
                                </S.FileUploadArea>
                                <S.HiddenInput ref={fileInputRef} type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileInputChange} />
                                {selectedFile && !isValidationSuccessful &&
                                (
                                    <S.SelectedFileInfo>
                                        <S.FileInfoText>
                                            <strong>{selectedFile.name}</strong> ({formatFileSize(selectedFile.size)}) - {selectedFile.content.length} registros
                                        </S.FileInfoText>
                                        <S.RemoveFileButton onClick={handleRemoveFile}>
                                            <DeleteIcon /> Quitar
                                        </S.RemoveFileButton>
                                    </S.SelectedFileInfo>
                                )}
                                {errors.length > 0 &&
                                (
                                    <S.ErrorMessage>
                                        <strong>Errores encontrados:</strong>
                                        <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                                            {errors.map((error, index) =>
                                            (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </S.ErrorMessage>
                                )}
                                {warnings.length > 0 &&
                                (
                                    <S.WarningMessage>
                                        <strong>Advertencias:</strong>
                                        <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                                            {warnings.map((warning, index) =>
                                            (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    </S.WarningMessage>
                                )}
                            </S.Section>
                        )
                        : currentStep === 'preview' ?
                        (
                            <S.Section>
                                <S.SectionTitle>Vista Previa de los Datos Importados</S.SectionTitle>
                                <S.FileInfoRow>
                                    <S.FileInfoText>
                                        <S.FileName>{selectedFile?.name}</S.FileName>
                                        <S.FileDetails>
                                            {formatFileSize(selectedFile?.size || 0)} • {importedData.length} registros • {fileType.toUpperCase()}
                                        </S.FileDetails>
                                    </S.FileInfoText>
                                    <S.RemoveFileButton onClick={handleRemoveFile} title="Quitar archivo">
                                        <CancelIcon />
                                    </S.RemoveFileButton>
                                </S.FileInfoRow>
                                <S.DataTableContainer>
                                    <S.TableWrapper>
                                        <S.DataTable>
                                            <S.TableHeader>
                                                <tr>
                                                    <S.TableHeaderCell>Fecha</S.TableHeaderCell>
                                                    <S.TableHeaderCell>ISIN</S.TableHeaderCell>
                                                    <S.TableHeaderCell>Peso (%)</S.TableHeaderCell>
                                                </tr>
                                            </S.TableHeader>
                                            <S.TableBody>
                                                {Object.entries(groupedData).map(([fecha, rows]) =>
                                                {
                                                    const totalWeight = rows.reduce((acc, r) => acc + r.weight, 0);
                                                    const isExpanded = expanded[fecha] ?? false;

                                                    return(
                                                        <React.Fragment key={fecha}>
                                                             {/* Fila resumen */}
                                                             <S.SummaryTableRow onClick={() => toggleGroup(fecha)} style={{cursor: "pointer"}}>
                                                                <S.TableCell>
                                                                    {isExpanded ?
                                                                    (
                                                                        <ChevronUpIcon style={{marginRight: 6}} />
                                                                    ) :
                                                                    (
                                                                        <ChevronDownIcon style={{marginRight: 6}} />
                                                                    )}
                                                                    {fecha}
                                                                </S.TableCell>
                                                                <S.TableCell>{rows.length} ISINs</S.TableCell>
                                                                 <S.TableCell>
                                                                    {totalWeight.toFixed(2).replace(".", ",")}%
                                                                 </S.TableCell>
                                                             </S.SummaryTableRow>
                                                            {/* Filas expandidas */}
                                                            {isExpanded && rows.map((row, idx) =>
                                                            (
                                                                <S.TableRow key={`${fecha}-${idx}`}>
                                                                    <S.TableCell /> {/* celda vacía */}
                                                                    <S.TableCell>{row.isin} {row.available ? '' : '(No disponible)'}</S.TableCell>
                                                                    <S.TableCell>
                                                                        {row.weight.toFixed(2).replace(".", ",")}%
                                                                    </S.TableCell>
                                                                </S.TableRow>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </S.TableBody>
                                        </S.DataTable>
                                    </S.TableWrapper>
                                </S.DataTableContainer>
                                {hasUnavailableFunds &&
                                (
                                    <S.WarningMessage>
                                        <strong>Advertencia:</strong> Uno o más fondos en la composición no están disponibles. 
                                        No permitimos la importación de fondos no disponibles.
                                    </S.WarningMessage>
                                )}
                                {portfolioModelModification.validationInfo?.isInRange &&
                                (
                                    <S.PortfolioModelModificationContainer>
                                        <S.ModificationToggleContainer>
                                            <S.ModificationLabel>¿Deseas actualizar tu Cartera Modelo?</S.ModificationLabel>
                                            <Toggle checked={portfolioModelModification.userRequestPortfolioModelChange}
                                                onChange={handlePortfolioModelModificationChange}
                                                onText="Sí"
                                                offText="No"
                                                styles={{root: {marginLeft: '12px', marginBottom: '0 !important'}}}
                                            />
                                        </S.ModificationToggleContainer>
                                        <S.ModificationInfoText>
                                            Marcando el SÍ indicas que deseas que el valor de la última composición actualice el valor de tu Cartera Modelo. 
                                            <br />
                                            <br />
                                            Esto afecta a todas las operaciones asociadas a tu Cartera Modelo, así como a las Aportaciones Periódicas 
                                            que esta tuviera a partir de este momento.
                                        </S.ModificationInfoText>
                                    </S.PortfolioModelModificationContainer>
                                )}
                            </S.Section>
                        ) :
                        !showSuccessMessage &&
                        (
                            <S.Section>
                                <S.SectionTitle>Selecciona los Elements para el pago</S.SectionTitle>
                                <S.FileInfoRow>
                                    <S.FileInfoText>
                                        <S.FileName>{selectedFile?.name}</S.FileName>
                                        <S.FileDetails>
                                            {formatFileSize(selectedFile?.size || 0)} • {importedData.length} registros • {fileType.toUpperCase()}
                                        </S.FileDetails>
                                    </S.FileInfoText>
                                    <S.RemoveFileButton onClick={handleRemoveFile} title="Quitar archivo">
                                        <CancelIcon />
                                    </S.RemoveFileButton>
                                </S.FileInfoRow>
                                <S.DataTableContainer>
                                    <S.TableWrapper>
                                        <S.DataTable>
                                            <S.TableHeader>
                                                <tr>
                                                    <S.TableHeaderCell style={{width: '40%'}}>Plan de Ahorro</S.TableHeaderCell>
                                                    <S.TableHeaderCell style={{width: '30%'}}>Elements IA Disponibles</S.TableHeaderCell>
                                                    <S.TableHeaderCell style={{width: '30%'}}>Elements IA utilizados</S.TableHeaderCell>
                                                </tr>
                                            </S.TableHeader>
                                            <S.TableBody>
                                                {editableData.length > 0 ? editableData.map((row, index) =>
                                                (
                                                    <S.TableRow key={index}>
                                                        <S.TableCell style={{width: '40%'}}>
                                                            {row.planAhorro || ''}
                                                        </S.TableCell>
                                                        <S.TableCell style={{width: '30%'}}>
                                                            {decimalFormatter.format(row.elementsDisponibles || 0)}
                                                        </S.TableCell>
                                                        <S.TableCell style={{width: '30%'}}>
                                                            <input type="number" value={row.elementsAUsar || 0} min="0"
                                                                onChange={(e) => handleEditableDataChange(index, 'elementsAUsar', parseInt(e.target.value) || 0)}
                                                                max={Math.max(0, row.elementsDisponibles || 0)}
                                                                disabled={(row.elementsDisponibles || 0) <= 0}
                                                                style={
                                                                {
                                                                    width: '100%',
                                                                    padding: '4px 8px',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: (row.elementsDisponibles || 0) <= 0 ? '#f5f5f5' : 'white',
                                                                    color: (row.elementsDisponibles || 0) <= 0 ? '#999' : 'inherit',
                                                                    cursor: (row.elementsDisponibles || 0) <= 0 ? 'not-allowed' : 'text'
                                                                }}
                                                            />
                                                        </S.TableCell>
                                                    </S.TableRow>
                                                )) :
                                                (
                                                    <S.TableRow>
                                                        <S.TableCell colSpan={3} style={{textAlign: 'center', padding: '20px'}}>
                                                            <p>No tienes ningún plan de ahorro activo con Elements. No puedes realizar esta acción.</p>
                                                        </S.TableCell>
                                                    </S.TableRow>
                                                )}
                                            </S.TableBody>
                                        </S.DataTable>
                                    </S.TableWrapper>
                                </S.DataTableContainer>
                                {editableData.length > 0 &&
                                (
                                    <S.ElementsSummaryContainer>
                                        <S.ElementsSummaryRow>
                                            <S.ElementsSummaryTitle>Total Elements IA necesarios:</S.ElementsSummaryTitle>
                                            <S.ElementsSummaryCounter $isValid={totalElementsAUsar > 0} $isExact={totalElementsAUsar === elementsIAPrice} $isExceeded={totalElementsAUsar > elementsIAPrice}>
                                                {totalElementsAUsar}/{elementsIAPrice}
                                            </S.ElementsSummaryCounter>
                                        </S.ElementsSummaryRow>
                                        {totalElementsAUsar > elementsIAPrice &&
                                        (
                                            <S.ElementsSummaryMessage $type="error">
                                                ⚠️ El total no puede exceder {elementsIAPrice} Elements IA
                                            </S.ElementsSummaryMessage>
                                        )}
                                        {totalElementsAUsar === elementsIAPrice &&
                                        (
                                            <S.ElementsSummaryMessage $type="success">
                                                ✅ Total exacto de {elementsIAPrice} Elements IA - Listo para importar
                                            </S.ElementsSummaryMessage>
                                        )}
                                        {totalElementsAUsar > 0 && totalElementsAUsar < elementsIAPrice &&
                                        (
                                            <S.ElementsSummaryMessage $type="info">
                                                ℹ️ Necesitas usar exactamente {elementsIAPrice} Elements IA. Faltan {elementsIAPrice - totalElementsAUsar} Elements IA
                                            </S.ElementsSummaryMessage>
                                        )}
                                        {totalElementsAUsar === 0 &&
                                        (
                                            <S.ElementsSummaryMessage $type="info">
                                                ℹ️ Selecciona los Elements IA que quieres usar. El total debe ser exactamente {elementsIAPrice} Elements IA
                                            </S.ElementsSummaryMessage>
                                        )}
                                    </S.ElementsSummaryContainer>
                                )}
                            </S.Section>
                        )}
                        {(isProcessing || isImporting) &&
                        (
                            <S.Section>
                                <S.SectionTitle>
                                    {isProcessing ? 'Procesando archivo...' : 'Importando composición...'}
                                </S.SectionTitle>
                                <S.SectionDescription>
                                    {isProcessing ? 
                                        'Por favor espera mientras procesamos tu archivo.' :
                                        'Procesando tus composiciones. Espera a que finalice el proceso.'
                                    }
                                </S.SectionDescription>
                            </S.Section>
                        )}
                        {showSuccessMessage && importResult?.status === true &&
                        (
                            <S.SuccessMessage>
                                <S.SuccessIcon>✓</S.SuccessIcon>
                                <span>{importResult.message}</span>
                            </S.SuccessMessage>
                        )}
                        {showErrorMessage && importResult?.status === false &&
                        (
                            <S.ServerErrorMessage>
                                <S.ErrorIcon>✗</S.ErrorIcon>
                                <span>{importResult.message}</span>
                                <button onClick={() =>
                                    {
                                        setShowErrorMessage(false);
                                        setImportResult(null);
                                    }}
                                    style=
                                    {{
                                        marginLeft: 'auto',
                                        padding: '4px 8px',
                                        backgroundColor: 'transparent',
                                        border: '1px solid #d13438',
                                        borderRadius: '4px',
                                        color: '#d13438',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Reintentar
                                </button>
                            </S.ServerErrorMessage>
                        )}
                    </S.ContentContainer>
                    <S.ButtonContainer>
                        <S.CancelButton onClick={handleClose} disabled={isImporting || showSuccessMessage || showErrorMessage}>
                            Cancelar
                        </S.CancelButton>
                        {currentStep === 'preview' ?
                        (
                            <S.ImportButton onClick={handleContinue} disabled={!canContinue || isImporting || showSuccessMessage || showErrorMessage}>
                            {
                                isProcessing ? 'Procesando...' : 
                                isImporting ? 'Importando...' :
                                showSuccessMessage ? 'Completado' :
                                showErrorMessage ? 'Error' :
                                hasUnavailableFunds ? 'Fondos no disponibles' : 'Continuar'
                            }
                            </S.ImportButton>
                        ) :
                        (
                            <S.ImportButton onClick={handleImport} disabled={!canEndImport || isImporting || showSuccessMessage || showErrorMessage}>
                            {
                                isImporting ? 'Importando...' :
                                showSuccessMessage ? 'Completado' :
                                showErrorMessage ? 'Error' :
                                totalElementsAUsar > elementsIAPrice ? 'Excede el límite' : 
                                totalElementsAUsar === 0 ? 'Selecciona Elements' : 
                                totalElementsAUsar === elementsIAPrice ? 'Importar' : 
                                `Faltan ${elementsIAPrice - totalElementsAUsar} Elements`
                            }
                            </S.ImportButton>
                        )}
                    </S.ButtonContainer>
                </S.ModalContainer>
            </Modal>
        </React.Fragment>
    );
};

export default ImportComposition;